'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Loader2, ShieldCheck, Flag } from 'lucide-react';
import { toast } from 'sonner';

type RelationshipType =
  | 'Partner' |'Ex' |'Crush' |'Friend' |'Family' |'Coworker' |'Someone Online' |'Other';

const RELATIONSHIP_OPTIONS: { label: string; emoji: string; value: RelationshipType }[] = [
  { label: 'Partner', emoji: '💑', value: 'Partner' },
  { label: 'Ex', emoji: '💔', value: 'Ex' },
  { label: 'Crush', emoji: '😍', value: 'Crush' },
  { label: 'Friend', emoji: '🤝', value: 'Friend' },
  { label: 'Family', emoji: '🏠', value: 'Family' },
  { label: 'Coworker', emoji: '💼', value: 'Coworker' },
  { label: 'Someone Online', emoji: '📱', value: 'Someone Online' },
  { label: 'Other', emoji: '❓', value: 'Other' },
];

interface FormValues {
  conversation: string;
}

export default function ConversationInputClient() {
  const router = useRouter();
  const [selectedRelationship, setSelectedRelationship] = useState<RelationshipType | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { conversation: '' } });

  const conversationValue = watch('conversation');
  const charCount = conversationValue?.length ?? 0;
  const MAX_CHARS = 8000;

  const onSubmit = async (data: FormValues) => {
    if (!selectedRelationship) {
      toast.error('Please select who this conversation is with.');
      return;
    }

    if (data.conversation.trim().length < 50) {
      toast.error('Please paste a longer conversation — at least a few messages.');
      return;
    }

    setIsAnalyzing(true);

    try {
      // BACKEND INTEGRATION: POST to /api/analyze with { conversation, relationshipType }
      // Returns structured JSON with free + premium analysis fields
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation: data.conversation.slice(0, MAX_CHARS),
          relationshipType: selectedRelationship,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || 'Analysis failed');
      }

      const result = await response.json();

      // Store analysisId separately for payment flow (premium data stays server-side)
      if (result.analysisId) {
        sessionStorage.setItem('rfr_analysis_id', result.analysisId);
      }

      // Store free result fields only in sessionStorage
      sessionStorage.setItem('rfr_analysis', JSON.stringify(result));
      sessionStorage.setItem('rfr_relationship', selectedRelationship);

      router.push('/analysis-results-screen');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <header className="pt-10 pb-2 px-5 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center glow-red-sm"
            style={{ background: 'var(--primary)' }}
          >
            <Flag size={20} color="white" strokeWidth={2.5} />
          </div>
          <span className="text-2xl font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>
            🚩 Red Flag Receipts
          </span>
        </div>
        <p
          className="text-sm font-medium tracking-wide"
          style={{ color: 'var(--muted-foreground)' }}
        >
          Upload the conversation. See what you might be missing.
        </p>
      </header>

      {/* Main Form */}
      <main className="flex-1 px-4 pb-10 pt-6 max-w-lg mx-auto w-full">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Conversation Textarea */}
          <div className="mb-5">
            <label
              htmlFor="conversation"
              className="block text-sm font-600 mb-2"
              style={{ color: 'var(--foreground)' }}
            >
              Paste your conversation
            </label>
            <div className="relative">
              <textarea
                id="conversation"
                {...register('conversation', {
                  required: 'Please paste a conversation to analyze.',
                  minLength: {
                    value: 50,
                    message: 'Paste at least a few messages for a meaningful analysis.',
                  },
                  maxLength: {
                    value: MAX_CHARS,
                    message: `Conversation too long — maximum ${MAX_CHARS.toLocaleString()} characters.`,
                  },
                })}
                placeholder="Paste your conversation here...&#10;&#10;You can copy directly from iMessage, WhatsApp, Instagram DMs, or any messaging app."
                rows={10}
                maxLength={MAX_CHARS}
                className="w-full resize-none text-sm leading-relaxed p-4 rounded-xl outline-none transition-all duration-200 placeholder-muted-foreground"
                style={{
                  background: 'var(--input)',
                  border: errors.conversation
                    ? '1.5px solid var(--primary)'
                    : '1.5px solid var(--border)',
                  color: 'var(--foreground)',
                  fontFamily: 'var(--font-sans)',
                }}
                onFocus={(e) => {
                  if (!errors.conversation) {
                    e.target.style.borderColor = 'var(--primary)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(229,62,62,0.12)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.conversation) {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              <div
                className="absolute bottom-3 right-3 text-xs font-medium"
                style={{
                  color: charCount > MAX_CHARS * 0.9 ? 'var(--primary)' : 'var(--muted-foreground)',
                }}
              >
                {charCount.toLocaleString()}/{MAX_CHARS.toLocaleString()}
              </div>
            </div>
            {errors.conversation && (
              <p className="mt-1.5 text-xs font-medium" style={{ color: 'var(--primary)' }}>
                {errors.conversation.message}
              </p>
            )}
          </div>

          {/* Relationship Type */}
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
              Who is this with?
            </p>
            <div className="flex flex-wrap gap-2">
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <button
                  key={`chip-${opt.value}`}
                  type="button"
                  onClick={() => setSelectedRelationship(opt.value)}
                  className="px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150"
                  style={
                    selectedRelationship === opt.value
                      ? {
                          background: 'var(--primary)',
                          color: 'var(--primary-foreground)',
                          border: '1.5px solid var(--primary)',
                        }
                      : {
                          background: 'transparent',
                          color: 'var(--muted-foreground)',
                          border: '1.5px solid var(--border)',
                        }
                  }
                >
                  {opt.emoji} {opt.label}
                </button>
              ))}
            </div>
            {!selectedRelationship && isAnalyzing && (
              <p className="mt-1.5 text-xs font-medium" style={{ color: 'var(--primary)' }}>
                Please select a relationship type.
              </p>
            )}
          </div>

          {/* Analyze Button */}
          <button
            type="submit"
            disabled={isAnalyzing}
            className="btn-red w-full py-4 text-base font-bold tracking-wide uppercase flex items-center justify-center gap-2 glow-red"
            style={{ letterSpacing: '0.05em' }}
          >
            {isAnalyzing ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Analyzing the Receipts...</span>
              </>
            ) : (
              <>
                <span>🚩</span>
                <span>Analyze the Receipts</span>
              </>
            )}
          </button>

          {/* Privacy Notice */}
          <div className="mt-5 flex items-start gap-2.5 px-1">
            <ShieldCheck
              size={16}
              className="mt-0.5 flex-shrink-0"
              style={{ color: 'var(--muted-foreground)' }}
            />
            <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              <span className="font-semibold" style={{ color: '#888' }}>
                Private by design.
              </span>{' '}
              Your conversation is analyzed for this result and is not intentionally saved. No account
              required.
            </p>
          </div>
        </form>

        {/* Demo link for testing */}
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => {
              // DEMO: Load mock analysis result for UI testing without real API
              const mockResult = {
                redFlagScore: 74,
                communicationScore: 42,
                consistencyScore: 38,
                respectScore: 55,
                biggestRedFlag: {
                  title: 'Dismissive Deflection',
                  explanation:
                    'Several messages appear to redirect serious questions back to you rather than addressing them directly, which may indicate avoidance of accountability.',
                },
                observations: [
                  {
                    id: 'obs-1',
                    text: 'The tone shifts noticeably when boundaries are mentioned — responses become shorter and less engaged.',
                  },
                  {
                    id: 'obs-2',
                    text: 'Apologies appear to be followed quickly by counter-complaints, which may indicate a pattern of deflection.',
                  },
                ],
                additionalPatternCount: 6,
                premium: {
                  receipts: [
                    {
                      id: 'receipt-1',
                      message: '"Why are you always so sensitive about everything?"',
                      explanation:
                        'This phrasing may shift responsibility for the emotional dynamic onto you, framing your feelings as the problem rather than the behavior that caused them.',
                    },
                    {
                      id: 'receipt-2',
                      message: '"I said I was sorry, what more do you want?"',
                      explanation:
                        'This response may indicate an expectation that an apology automatically resolves the issue, without addressing the underlying concern.',
                    },
                    {
                      id: 'receipt-3',
                      message: '"You\'re reading into things that aren\'t there."',
                      explanation:
                        'Dismissing your interpretation without engaging with it may be a form of minimization — it\'s possible this reflects genuine disagreement, or it may indicate an attempt to avoid the topic.',
                    },
                  ],
                  redFlagPatterns: [
                    {
                      id: 'pattern-1',
                      title: 'Minimization',
                      description:
                        'There appear to be several instances where your concerns are described as overreactions or misunderstandings without substantive engagement.',
                    },
                    {
                      id: 'pattern-2',
                      title: 'Reactive Apologies',
                      description:
                        'Apologies in this conversation appear to be followed quickly by justifications, which may reduce their sincerity.',
                    },
                  ],
                  mixedSignals: [
                    {
                      id: 'signal-1',
                      description:
                        'Warmth and affection appear in some messages, while dismissiveness appears in others — this inconsistency may create confusion about where you stand.',
                    },
                    {
                      id: 'signal-2',
                      description:
                        'Statements about wanting to communicate openly are present, yet direct questions appear to go unanswered in multiple instances.',
                    },
                  ],
                  pressurePatterns: [
                    {
                      id: 'pressure-1',
                      description:
                        'There are possible instances of guilt-framing — language that positions your expectations as burdensome may be present.',
                    },
                  ],
                  avoidance: [
                    {
                      id: 'avoid-1',
                      topic: 'Your question about plans',
                      description: 'This topic appears to have been redirected without a direct answer.',
                    },
                    {
                      id: 'avoid-2',
                      topic: 'The previous argument',
                      description: 'References to a prior conflict appear to have been minimized rather than addressed.',
                    },
                  ],
                  powerBalance:
                    'In this conversation, you appear to be the one seeking clarity and resolution, while the other person appears to redirect, withdraw, or reframe more frequently. This pattern may create an imbalance where one person carries more of the emotional labor.',
                  interpretations: [
                    {
                      id: 'interp-1',
                      label: 'Possible Interpretation A',
                      text: 'The other person may be genuinely overwhelmed and using deflection as an unconscious coping mechanism, not as an intentional manipulation tactic.',
                    },
                    {
                      id: 'interp-2',
                      label: 'Possible Interpretation B',
                      text: 'This communication style may be a learned pattern from their past that they haven\'t examined — it may not be directed at you personally.',
                    },
                    {
                      id: 'interp-3',
                      label: 'Possible Interpretation C',
                      text: 'Some of these patterns may reflect a fundamental mismatch in communication styles rather than bad intent.',
                    },
                  ],
                  responses: {
                    keepItClassy:
                      "I've noticed that when I bring up concerns, the conversation tends to shift away from what I'm asking. I'd really like us to be able to talk through things directly — can we try that?",
                    callItOut:
                      "I need to be honest — it feels like my questions keep getting turned around on me instead of answered. That's not a conversation, that's deflection. I need you to actually hear me.",
                    endConversation:
                      "I don't think this conversation is going anywhere productive right now. I'm going to step back. If things change and you want to talk openly, I'm open to that.",
                  },
                },
              };
              sessionStorage.setItem('rfr_analysis', JSON.stringify(mockResult));
              sessionStorage.setItem('rfr_relationship', 'Partner');
              router.push('/analysis-results-screen');
            }}
            className="text-xs underline underline-offset-2"
            style={{ color: 'var(--muted-foreground)' }}
          >
            View demo result (no API needed)
          </button>
        </div>
      </main>
    </div>
  );
}